import React, { useRef, useState } from 'react';
import { TextArea } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { approveResidentialUnitDocument } from '@/services/documents';
import { AlertBanner, Button, ButtonBar, Checkbox, Dialog } from '@ygtechnologies/rubus-ui';
import DocumentResidentialUnitSelection from './DocumentResidentialUnitSelection';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import theme from './DocumentApproveDialog.module.scss';

interface OwnProps {
    documentVersionId: number;
    residentialUnitId: number | null;
    onClose(): void;
    onApprove(residentialUnitIds: number[], params: {}): void;
    show: boolean;
    hiddenFromBuyer: boolean;
}

const DocumentApproveDialog: React.FC<OwnProps> = ({
    documentVersionId,
    residentialUnitId,
    onClose,
    onApprove,
    show,
    hiddenFromBuyer,
}) => {
    const [saving, setSaving] = useState(false);
    const [selectedResidentialUnitIds, setSelectedResidentialUnitIds] = useState<number[]>([]);
    const [sendEmail, setSendEmail] = useState(!hiddenFromBuyer);
    const [emailMessage, setEmailMessage] = useState('');
    const focusElementRef = useRef<HTMLTextAreaElement>(null);
    const projectId = useSelector((state: IState) => state.global.activeProject!.id);

    const handleOpen = () => {
        setSaving(false);
        setSelectedResidentialUnitIds([]);

        if (focusElementRef.current) {
            focusElementRef.current.focus();
        }
    };

    const handleSave = async (e) => {
        setSaving(true);

        try {
            await approveResidentialUnitDocument(documentVersionId, selectedResidentialUnitIds, sendEmail, emailMessage);

            triggerHandler(onApprove, selectedResidentialUnitIds, {});
            triggerHandler(onClose);
        } finally {
            setSaving(false);
        }
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    return (
        <Dialog open={show} onOpenChange={(open) => !open && handleCancelClick()} onOpen={handleOpen}>
            <Dialog.Content contentWidth="lg">
                <Dialog.Header>
                    <Trans>Publish document</Trans>
                </Dialog.Header>
                <AlertBanner type="info">
                    <Trans>
                        <strong>Please note</strong> - when you publish this document it will be visible to the buyer of the
                        unit.
                    </Trans>
                </AlertBanner>
                <DocumentResidentialUnitSelection
                    projectId={projectId}
                    documentVersionId={documentVersionId}
                    residentialUnitId={residentialUnitId}
                    onChange={setSelectedResidentialUnitIds}
                    // value={selectedResidentialUnitIds}
                    singleCaption={<Trans>Publish for this unit only</Trans>}
                    allCaption={<Trans>Publish for all relevant units</Trans>}
                    multiCaption={<Trans>Publish for specific units</Trans>}
                />
                <Checkbox
                    inputSize="md"
                    className={theme.emailCheckbox}
                    disabled={hiddenFromBuyer}
                    checked={sendEmail}
                    onChange={() => setSendEmail(!sendEmail)}>
                    <Trans>Send email notifying buyer about the new document.</Trans>
                </Checkbox>

                {sendEmail && (
                    <label className="full-width">
                        <Trans>Message</Trans>
                        <TextArea
                            className="form-control"
                            onChange={(e) => setEmailMessage(e.target.value)}
                            value={emailMessage}
                        />
                    </label>
                )}
                <ButtonBar>
                    <ButtonBar.Right>
                        <Button variant="tertiary" onClick={handleCancelClick} disabled={saving}>
                            <Trans>Cancel</Trans>
                        </Button>
                        <Button
                            disabled={selectedResidentialUnitIds.length === 0}
                            variant="success"
                            onClick={handleSave}
                            loading={saving}>
                            <Trans>Publish</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </Dialog.Content>
        </Dialog>
    );
};

export default DocumentApproveDialog;